import { faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { useMeeting } from '../../../../../hooks/useMeeting';
import { useRole } from '../../../../../hooks/useRole';
import { Meetingroom, Network } from '../../../../../interfaces/interfaces';
import { NetworkComponentInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../../services/helper';
import { MeetingroomDetailsModal } from '../../../../account/meetingrooms/MeetingroomDetailsModal';
import InlineEditButton from '../../../pageBuilder/InlineEditButton';
import NetworkRoomInlineEdit from './NetworkRoomInlineEdit';
import UserSlot from './UserSlot';
import { useWindowSize } from '../../../../../hooks/useWindowSize';

interface Props {
  network: Network;
  uiComponent?: NetworkComponentInterface;
}

const MAX_VISIBLE = 18;

const NetworkRoom = (props: Props) => {
  const { network, uiComponent } = props;
  const [isEditMode, setIsEditMode] = useState(false);
  const [maxVisible, setMaxVisible] = useState(MAX_VISIBLE);
  const meeting = useMeeting();
  const { isUserEventHost } = useRole();
  const { t } = useTranslation();
  const { width } = useWindowSize();

  const templateEditMode = useSelector((state) => state.common.templateEditMode);

  const [showEditModal, setShowEditModal] = useState(false);

  const hosts = network.users.filter((user) => network.booth && user.organization && user.organization === network.booth?.organization);
  const users = network.users.filter((user) => !user.organization || !network.booth || user.organization !== network.booth?.organization);

  function checkHostPresent() {
    if (network?.settings?.joinwithoutadmin === false) return hosts?.length > 0;
    return true;
  }

  useEffect(() => {
    setMaxVisible(width && width < 640 ? 6 : MAX_VISIBLE);
  }, [width]);

  if (isEditMode && templateEditMode && isUserEventHost()) return <NetworkRoomInlineEdit setIsEditMode={setIsEditMode} {...props} />;

  return (
    <>
      <div
        className={`shadow-lg max-w-[600px] flex-col p-px inline-flex w-full text-left mb-8 relative`}
        style={{
          backgroundColor: uiComponent?.customStyling?.cardBackgroundColor || '#ffffff',
        }}>
        {network.bannerImage && (
          <img
            style={{ borderColor: uiComponent?.customStyling?.bannerBorderColor || '#ffffff' }}
            className="mb-1 border-2"
            src={createDisplayLink(network.bannerImage)}
          />
        )}
        <div className="flex flex-col justify-between p-4 h-full break-normal">
          <div>
            <div className="flex items-center">
              {network.image && <Avatar src={createDisplayLink(network.image)} className="mr-1 mb-1 templateBorder" />}
              <h1 className="text-2xl font-bold">{network.name}</h1>
            </div>
            <p style={{ whiteSpace: 'pre-line' }}>{network.description}</p>
            <div className="grid gap-3 mt-2" style={{ gridTemplateColumns: 'repeat(auto-fit, 64px)' }}>
              {hosts.map((host, index) => (
                <UserSlot key={`host-slot-${index}`} user={host} isRoomHost />
              ))}
              {new Array(Math.min(maxVisible, network.settings.userlimit)).fill(null).map((_, index) => {
                if (index < maxVisible - 1) return <UserSlot key={'user-slot-' + index} user={users[index]} />;
                if (uiComponent && !uiComponent.showCount) return <></>;
                return (
                  <div
                    key={'user-slot-empty-' + index}
                    style={{ color: '#cfcfcf' }}
                    className="grid place-items-center w-16 h-16 text-3xl whitespace-nowrap rounded-md">
                    +{network.settings.userlimit - maxVisible}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-between mt-2">
            <div>
              <p className="text-sm">{network.subtitle}</p>
              <p className="text-lg font-bold">
                {network.subtext}
                <span className="text-xs font-normal">
                  {users.length + hosts.length}/{network.settings.userlimit + hosts.length}
                </span>
              </p>
            </div>
            {network.settings.userlimit > users.length && (
              <button
                disabled={!checkHostPresent() && !isUserEventHost()}
                className={`self-center px-5 py-1 rounded disabled:opacity-50 disabled:cursor-default templateBorder network-button whitespace-nowrap`}
                style={{
                  backgroundColor: uiComponent?.customStyling?.buttonBackgroundColor || 'black',
                  color: uiComponent?.customStyling?.buttonTextColor || '#ffffff',
                }}
                onClick={() => {
                  // Converting to unknown first because Network interface lacks overlaps with Meetingroom interface
                  meeting.redirectToRoom((network as unknown) as Meetingroom);
                }}>
                + {t('common:join')}
              </button>
            )}
          </div>
          {isUserEventHost() && templateEditMode && <InlineEditButton setIsEditMode={setIsEditMode} isEditMode={isEditMode} tooltip="" forceShow />}
          {isUserEventHost() && templateEditMode && (
            <button
              className="grid absolute top-2 right-12 z-40 place-items-center mr-1 w-10 h-10 text-black bg-white bg-opacity-75 rounded-full"
              onClick={() => setShowEditModal(true)}>
              <FontAwesomeIcon icon={faCog} size="lg" />
            </button>
          )}
        </div>
        {showEditModal && <MeetingroomDetailsModal open={true} meeting={(network as unknown) as Meetingroom} onClose={() => setShowEditModal(false)} />}
      </div>
    </>
  );
};

export default NetworkRoom;
